import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { View, Text, TextInput, StyleSheet } from 'react-native';
import { Search } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useMapAreaId } from '../../hooks/useMapAreaId';
import { usePeopleListsStore } from '../../store/peopleListsStore';
import { PanelStack } from '../layout/PanelStack';
import { DEFENSE_STRATEGIES } from './defenseStrategies';
import { PLAN_PARTY_TYPE_META, partyDisplayName } from './planParties';
import { AssignPersonRow, type AssignRowDetail } from './AssignPersonRow.web';
import { featureStatusDisplay } from '../../utils/planAssignmentSummary';
import { matchExistingPartyForContact } from '../../utils/areaPeopleMatch';
import {
  categoryToPartyType,
  filterListedPeople,
  flattenLists,
  listsForArea,
  personMeta,
  splitByTeam,
  type ListedPerson,
} from '../../utils/peopleLists';
import type {
  DefensePlanFeature,
  DefensePlanAssignment,
  PlanParty,
  PlanBundleTeamMember,
  PlanAssignmentStatus,
} from '../../types';

// Owner-only editor for who's responsible for one defense or one bundle, the
// body half of the assign surface (panel-system.md Phase 1). Hosted either by
// a CompanionPanel on the desktop shell (DefensePlanPanel.web.tsx,
// DefensePlanViewer.web.tsx) or by DefensePlanAssignSheet.web.tsx's
// AdaptiveSheet on phone. Renders plain content (no ScrollView of its own):
// the host provides the single scrollable region.
//
// Oct 2026 rebuild (Glenn: "make it look like my Team list"). The list is the
// People panel's: "Team", then "Keep informed", from the owner's uploaded and
// hand-added lists. A tap on a person assigns them, a tap again removes them,
// and a ticked person opens in place for what they do, their status, an email
// for updates and (in a bundle) the leader control. Nothing here saves
// through a button; every action writes at once. New people are added in the
// People panel, not here.
//
// "On this plan" keeps whoever is on the plan but on none of the lists (old
// typed-in parties, volunteers) reachable, so nothing is orphaned.
//
// A list person's email is only ever offered, as a one-tap suggestion in the
// ticked row. A Cloud Function emails every address in a plan's
// partyContacts, so an address is stored only when the owner taps it or types
// and confirms one (AssignPersonRow's EmailField).

/** Identifies a bundle for the form's bundle mode — see the component doc
 *  comment below. */
export interface AssignBundleTarget {
  id: string;
  name: string;
  memberCount: number;
  /** The bundle's team, stored or derived (effectiveBundleTeam). */
  team?: PlanBundleTeamMember[];
  /** The leader's party id; always on `team` when set. */
  leaderPartyId?: string;
}

/** What an Assign view is about, in a few words: the bundle's name, the
 *  feature's plan label ("Fire break 2") or its strategy. Shared by the
 *  companion header ("Assign · Thin trees") and the form's own question on
 *  phone, so both name the object the same way. */
export function assignTargetLabel(
  feature: DefensePlanFeature | null,
  bundle: AssignBundleTarget | null | undefined,
  featureLabel?: string
): string {
  if (bundle) return bundle.name;
  if (feature) return featureLabel ?? DEFENSE_STRATEGIES[feature.strategy].label;
  return 'this defense';
}

export interface DefensePlanAssignFormProps {
  planId: string | null;
  /** The single defense being assigned — omit when `bundle` is set. */
  feature: DefensePlanFeature | null;
  /** Bundle mode (Sept 16 2026 — "bundle steps"): edits the bundle's team
   *  (Oct 5 2026), which links each teammate to every member defense via
   *  setOwnerAssignment (see defensePlansStore.addBundleTeammate) rather than
   *  one assignment record. */
  bundle?: AssignBundleTarget | null;
  /** Every party on this plan, keyed by id. */
  parties: Record<string, PlanParty>;
  /** This feature's current links, newest first. In bundle mode: the links on
   *  the bundle's member defenses, which each teammate's status rolls up
   *  from. */
  links: DefensePlanAssignment[];
  /** The plan's areaId: picks which of the owner's lists show. */
  areaId?: string | null;
  onClose: () => void;
  /** Opts the PanelStack into browser history (panel-system.md's "Deep
   *  links and back", Phase 4A) — the desktop-shell hosts (DefensePlanPanel,
   *  DefensePlanViewer) pass `true`; the phone AdaptiveSheet host
   *  (DefensePlanAssignSheet) leaves it unset. */
  historyEnabled?: boolean;
  /** The feature's plan label ("Fire break 2"), when the host has one. */
  featureLabel?: string;
  /** The host's header already names the object ("Assign · Tree
   *  thinning", the desktop companion), so the root question drops it:
   *  "Who's responsible?". The phone sheet has no header and leaves this
   *  off, so its question still says what it's about. */
  objectInHeader?: boolean;
}

export function DefensePlanAssignForm(props: DefensePlanAssignFormProps) {
  return (
    <PanelStack
      root={<AssignRoot {...props} />}
      historyKey={props.historyEnabled ? 'assign' : undefined}
    />
  );
}

function Overline({ children, first }: { children: string; first?: boolean }) {
  const { colors } = useTheme();
  return (
    <Text style={[styles.overline, first && styles.overlineFirst, { color: colors.textSecondary }]}>
      {children}
    </Text>
  );
}

/** Names match case-insensitively, trimmed. Both sides are cut to the 80
 *  characters createOwnerParty keeps, or a long name would never match the
 *  party it created. */
const PARTY_NAME_MAX = 80;

function openPeoplePanel() {
  const ui = useUIStore.getState();
  if (!ui.peoplePanelOpen) ui.togglePeoplePanel();
}

function AssignRoot({
  planId,
  feature,
  bundle,
  parties,
  links,
  areaId,
  onClose,
  featureLabel,
  objectInHeader = false,
}: DefensePlanAssignFormProps) {
  const { colors } = useTheme();
  // The People panel is a desktop-shell surface; on phone there is nowhere to
  // open it, so its buttons stay off.
  const canOpenPeople = useDesktopShell();
  const uid = useAuthStore((s) => s.user?.id ?? null);
  const lists = usePeopleListsStore((s) => s.lists);
  const listsLoaded = usePeopleListsStore((s) => s.listsLoaded);
  const peopleByList = usePeopleListsStore((s) => s.peopleByList);
  const subscribeLists = usePeopleListsStore((s) => s.subscribeLists);
  const retainPeople = usePeopleListsStore((s) => s.retainPeople);
  const setPersonTeam = usePeopleListsStore((s) => s.setTeam);
  const setOwnerAssignment = useDefensePlansStore((s) => s.setOwnerAssignment);
  const addBundleTeammate = useDefensePlansStore((s) => s.addBundleTeammate);
  const removeBundleTeammate = useDefensePlansStore((s) => s.removeBundleTeammate);
  const setBundleLeader = useDefensePlansStore((s) => s.setBundleLeader);
  const setBundleTeammateStatus = useDefensePlansStore((s) => s.setBundleTeammateStatus);
  const setBundleTeammateDescription = useDefensePlansStore((s) => s.setBundleTeammateDescription);
  const removeAssignment = useDefensePlansStore((s) => s.removeAssignment);
  const createOwnerParty = useDefensePlansStore((s) => s.createOwnerParty);
  const saveError = useDefensePlansStore((s) => s.saveError);

  const isBundleMode = !!bundle;
  const bundleTeam = bundle?.team ?? [];
  const bundleLeaderId = bundle?.leaderPartyId;

  const [search, setSearch] = useState('');
  // Rows with a write in flight: the target state to show meanwhile. The ref
  // is the synchronous double-tap guard; the state redraws the row.
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const inFlight = useRef(new Set<string>());
  // People moved to Team by ticking them stay in the section they were in
  // while this form is open, so a ticked row doesn't jump away from under
  // the pointer. A ref, read during render, because it must be set before the
  // store update that would otherwise move the row.
  const heldSection = useRef(new Map<string, boolean>());

  useEffect(() => {
    if (uid) subscribeLists(uid);
  }, [uid, subscribeLists]);

  // The lists the People panel shows right now (the area under the map),
  // plus any saved for the plan's own area. People's rule comes first on
  // purpose: someone the owner can see in People must be someone they can
  // assign here, even when the plan's area and the map view disagree.
  const mapAreaId = useMapAreaId();
  const visibleLists = useMemo(() => {
    const shown = listsForArea(lists, mapAreaId);
    const forPlan = listsForArea(lists, areaId ?? '').filter((l) => !shown.includes(l));
    return [...shown, ...forPlan];
  }, [lists, areaId, mapAreaId]);
  const visibleKey = visibleLists.map((l) => l.id).join(',');
  useEffect(() => {
    const releases = visibleKey ? visibleKey.split(',').map((id) => retainPeople(id)) : [];
    return () => releases.forEach((release) => release());
  }, [visibleKey, retainPeople]);

  // The form is reused when the host switches to another defense or bundle
  // without closing (pressing "Assign" on another row), so start clean then.
  const targetKey = bundle ? `b:${bundle.id}` : `f:${feature?.id ?? ''}`;
  useEffect(() => {
    setSearch('');
    heldSection.current.clear();
  }, [targetKey]);

  // Who is assigned here: a bundle's team, or the links on this defense.
  const linkByParty = new Map(links.map((l) => [l.partyId, l]));
  const teamByParty = new Map(bundleTeam.map((t) => [t.partyId, t]));
  const assignedIds = new Set(isBundleMode ? teamByParty.keys() : linkByParty.keys());

  const { listRows, onPlanParties } = useMemo(() => {
    const all = flattenLists(visibleLists, peopleByList);
    const matched = new Set<string>();
    const rows = all.map((entry) => {
      const party = matchExistingPartyForContact(parties, {
        name: entry.person.name.slice(0, PARTY_NAME_MAX),
        organization: entry.person.organization?.slice(0, PARTY_NAME_MAX),
      });
      if (party) matched.add(party.id);
      return { entry, party };
    });
    const unmatched = Object.values(parties)
      .filter((p) => !matched.has(p.id))
      .sort((a, b) =>
        partyDisplayName(a).localeCompare(partyDisplayName(b), undefined, { sensitivity: 'base' })
      );
    return { listRows: rows, onPlanParties: unmatched };
  }, [visibleLists, peopleByList, parties]);

  const q = search.trim().toLowerCase();
  const partyMatches = (p: PlanParty) =>
    !q ||
    partyDisplayName(p).toLowerCase().includes(q) ||
    PLAN_PARTY_TYPE_META[p.type].label.toLowerCase().includes(q);
  const shownOnPlan = onPlanParties.filter(partyMatches);

  const partyByEntry = new Map(listRows.map((r) => [r.entry.person.id, r.party]));
  const { team, informed } = splitByTeam(
    filterListedPeople(
      listRows.map((r) => r.entry),
      search
    ).map((entry) => {
      const held = heldSection.current.get(entry.person.id);
      return held === undefined ? entry : { ...entry, person: { ...entry.person, team: held } };
    })
  );

  // --- Writes ---------------------------------------------------------------

  const assign = async (partyId: string) => {
    if (!planId) return;
    if (bundle) {
      await addBundleTeammate(bundle.id, partyId, { status: 'asked', leader: false });
      if (!useDefensePlansStore.getState().saveError) {
        useUIStore.getState().flagJustAssignedBundle(bundle.id);
      }
    } else if (feature) {
      await setOwnerAssignment(planId, feature.id, partyId, { status: 'asked' });
      if (!useDefensePlansStore.getState().saveError) {
        useUIStore.getState().flagJustAssigned(feature.id);
      }
    }
  };

  const unassign = async (partyId: string) => {
    if (!planId) return;
    if (bundle) {
      await removeBundleTeammate(bundle.id, partyId);
    } else {
      const link = linkByParty.get(partyId);
      if (link) await removeAssignment(planId, link.id);
    }
  };

  /** Runs one row's write once at a time; a second tap while it's in flight
   *  is dropped. */
  const guarded = async (key: string, target: boolean, run: () => Promise<void>) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setBusy((b) => ({ ...b, [key]: target }));
    try {
      await run();
    } finally {
      inFlight.current.delete(key);
      setBusy((b) => {
        const rest = { ...b };
        delete rest[key];
        return rest;
      });
    }
  };

  const toggleListPerson = (entry: ListedPerson, party: PlanParty | null, checked: boolean) =>
    guarded(entry.person.id, !checked, async () => {
      if (!planId) return;
      const { person, listId } = entry;
      if (checked) {
        if (party) await unassign(party.id);
        return;
      }
      // Never the list's email: an address from a list is only a suggestion
      // in the ticked row.
      const partyId =
        party?.id ??
        (await createOwnerParty(
          planId,
          {
            type: categoryToPartyType(person.category),
            organization: person.organization,
            person: person.name,
          },
          undefined
        ));
      if (!partyId) return;
      await assign(partyId);
      // A person from Keep informed moves to Team once the save is clean.
      if (!useDefensePlansStore.getState().saveError && !person.team) {
        heldSection.current.set(person.id, false);
        setPersonTeam(listId, person.id, true).catch(() => {});
      }
    });

  const toggleParty = (party: PlanParty, checked: boolean) =>
    guarded(`party:${party.id}`, !checked, () => (checked ? unassign(party.id) : assign(party.id)));

  // --- Rows -----------------------------------------------------------------

  const detailFor = (partyId: string, suggestedEmail?: string): AssignRowDetail | null => {
    if (!planId) return null;
    const base = { planId, partyId, suggestedEmail };
    if (bundle) {
      const member = teamByParty.get(partyId);
      const own = links.filter((l) => l.partyId === partyId);
      const uniform =
        own.length > 0 && own.every((l) => l.status === own[0].status) ? own[0].status : null;
      // Mixed statuses read as text, since no one segment can be selected.
      const mixed = own.length > 1 && !uniform ? featureStatusDisplay(own) : null;
      const isLeader = partyId === bundleLeaderId;
      return {
        ...base,
        description: member?.description,
        status: uniform,
        mixedText: mixed?.text,
        onDescription: (description) =>
          void setBundleTeammateDescription(bundle.id, partyId, description),
        onStatus: (status) => void setBundleTeammateStatus(bundle.id, partyId, status),
        leader: {
          isLeader,
          onChange: (next) => void setBundleLeader(bundle.id, next ? partyId : null),
        },
      };
    }
    const link = linkByParty.get(partyId);
    if (!link || !feature) return null;
    return {
      ...base,
      description: link.description,
      status: link.status,
      onDescription: (description) =>
        void setOwnerAssignment(planId, feature.id, partyId, {
          description,
          status: link.status,
        }),
      onStatus: (status: PlanAssignmentStatus) =>
        void setOwnerAssignment(planId, feature.id, partyId, {
          description: link.description,
          status,
        }),
    };
  };

  const renderRow = (props: {
    key: string;
    pickTestID: string;
    busyKey: string;
    name: string;
    meta: string;
    partyId: string | null;
    suggestedEmail?: string;
    onToggle: (checked: boolean) => void;
  }) => {
    const derived = props.partyId != null && assignedIds.has(props.partyId);
    const checked = busy[props.busyKey] ?? derived;
    return (
      <AssignPersonRow
        key={`${targetKey}:${props.key}`}
        pickTestID={props.pickTestID}
        rowTestID={
          isBundleMode && checked && props.partyId ? `assign-team-${props.partyId}` : undefined
        }
        name={props.name}
        meta={props.meta}
        checked={checked}
        isLeader={isBundleMode && props.partyId != null && props.partyId === bundleLeaderId}
        onToggle={() => props.onToggle(derived)}
        detail={checked && props.partyId ? detailFor(props.partyId, props.suggestedEmail) : null}
      />
    );
  };

  const personRow = (entry: ListedPerson) => {
    const party = partyByEntry.get(entry.person.id) ?? null;
    const { person } = entry;
    return renderRow({
      key: person.id,
      pickTestID: `assign-pick-${person.id}`,
      busyKey: person.id,
      name: person.name,
      meta: personMeta(person) || person.category || '',
      partyId: party?.id ?? null,
      suggestedEmail: person.emails[0],
      onToggle: (checked) => void toggleListPerson(entry, party, checked),
    });
  };

  const partyRow = (party: PlanParty) =>
    renderRow({
      key: `party:${party.id}`,
      pickTestID: `assign-pick-party-${party.id}`,
      busyKey: `party:${party.id}`,
      name: partyDisplayName(party),
      meta: PLAN_PARTY_TYPE_META[party.type].label,
      partyId: party.id,
      onToggle: (checked) => void toggleParty(party, checked),
    });

  const targetLabel = assignTargetLabel(feature, bundle, featureLabel);
  // The sticky footer needs its host's fill behind it: the companion is
  // surfaceAlt; the phone sheet is surfaceElevated.
  const footerBackground = objectInHeader ? colors.surfaceAlt : colors.surfaceElevated;

  const hasAnyone = visibleLists.length > 0 || Object.keys(parties).length > 0;
  const hasLists = visibleLists.length > 0;
  const nothingMatches =
    q.length > 0 && shownOnPlan.length === 0 && team.length === 0 && informed.length === 0;
  // Wait for the first lists snapshot so the empty state doesn't flash.
  const showEmpty = !hasAnyone && (listsLoaded || !uid);

  const sections: {
    key: string;
    label: string;
    count: number;
    rows: ReactNode[];
    empty?: string;
  }[] = [];
  if (shownOnPlan.length > 0) {
    sections.push({
      key: 'plan',
      label: 'On this plan',
      count: shownOnPlan.length,
      rows: shownOnPlan.map(partyRow),
    });
  }
  if (hasLists) {
    sections.push({
      key: 'team',
      label: 'Team',
      count: team.length,
      rows: team.map(personRow),
      empty: 'No one yet. Move people here in the People panel.',
    });
    sections.push({
      key: 'informed',
      label: 'Keep informed',
      count: informed.length,
      rows: informed.map(personRow),
    });
  }

  return (
    <View style={[styles.content, objectInHeader && styles.contentCompanion]}>
      {/* The desktop companion's header already says "Assign / Tree
          thinning / Who's responsible?"; only the phone sheet, which has no
          header, asks here. */}
      {!objectInHeader && (
        <Text style={[styles.title, bundle && styles.titleWithApplies, { color: colors.text }]}>
          {`Who's responsible for ${targetLabel}?`}
        </Text>
      )}
      {bundle && (
        <Text
          style={[styles.appliesLine, { color: colors.textSecondary }]}
          testID="assign-bundle-applies">
          Applies to all {bundle.memberCount} in this bundle.
        </Text>
      )}

      {showEmpty ? (
        <View style={styles.empty} testID="assign-empty">
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            No people yet. Add the people you work with, then assign them here.
          </Text>
          {canOpenPeople && (
            <Button
              title="Open People"
              onPress={openPeoplePanel}
              variant="secondary"
              size="sm"
              testID="assign-open-people"
            />
          )}
        </View>
      ) : (
        <>
          <View style={[styles.searchRow, { borderColor: colors.border }]}>
            <Search size={14} color={colors.textTertiary} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search name, organization or type"
              placeholderTextColor={colors.textTertiary}
              testID="assign-search"
              accessibilityLabel="Search people"
              style={[styles.searchInput, { color: colors.text }]}
            />
          </View>

          <View testID={isBundleMode ? 'assign-team-list' : 'assign-list'}>
            {nothingMatches ? (
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                No one matches that search.
              </Text>
            ) : (
              sections.map((section, i) => (
                <View key={section.key} testID={`assign-section-${section.key}`}>
                  <Overline first={i === 0}>{`${section.label} (${section.count})`}</Overline>
                  {section.rows.length === 0 && section.empty ? (
                    <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                      {section.empty}
                    </Text>
                  ) : (
                    section.rows
                  )}
                </View>
              ))
            )}
          </View>

          {canOpenPeople && (
            <View style={styles.manage}>
              <TextLink
                label="Manage people"
                onPress={openPeoplePanel}
                testID="assign-open-people"
                textStyle={TYPOGRAPHY.caption}
              />
            </View>
          )}
        </>
      )}

      {saveError && (
        <Text style={[styles.error, { color: colors.danger }]} testID="assign-save-error">
          {saveError}
        </Text>
      )}

      {/* Sticky, so Done stays in reach however long the list gets.
          position: sticky is web-only, which is all this .web file renders
          on. A lone footer action is sm and full width in a desktop panel;
          the phone sheet keeps md for the 44px touch target. */}
      <View
        style={[
          styles.footer,
          { backgroundColor: footerBackground, borderTopColor: colors.border },
        ]}>
        <Button
          title="Done"
          onPress={onClose}
          variant="secondary"
          size={objectInHeader ? 'sm' : 'md'}
          testID="plan-assign-done"
          fullWidth
        />
      </View>

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="assign-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: SPACING.lg,
    paddingTop: SPACING.lg,
  },
  // In the desktop companion: the header's side padding, so the rows line
  // up under the title (the phone sheet keeps the sheet padding above).
  contentCompanion: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  titleWithApplies: {
    marginBottom: SPACING.xs,
  },
  appliesLine: {
    ...TYPOGRAPHY.caption,
    marginBottom: SPACING.md,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    marginBottom: SPACING.sm,
  },
  searchInput: {
    ...TYPOGRAPHY.body,
    flex: 1,
    paddingVertical: SPACING.sm,
  },
  // 24px above a section label, 8px from it to its content (first section 0).
  overline: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.lg,
    marginBottom: SPACING.sm,
  },
  overlineFirst: {
    marginTop: 0,
  },
  empty: {
    gap: SPACING.sm,
    alignItems: 'flex-start',
  },
  emptyText: {
    ...TYPOGRAPHY.bodySmall,
  },
  manage: {
    marginTop: SPACING.md,
    alignItems: 'flex-start',
  },
  error: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.sm,
  },
  footer: {
    marginTop: SPACING.md,
    paddingVertical: SPACING.smd,
    borderTopWidth: StyleSheet.hairlineWidth,
    position: 'sticky' as 'relative',
    bottom: 0,
  },
  scrollSentinel: {
    height: 1,
  },
});
